/**
 * simulatedTreeStorage.js
 *
 * Stores Talk Trees created in the Tree Simulator in a browser cookie so
 * they can be listed in SessionTrees without a backend save. Trees are kept
 * as a single JSON array under one cookie key, capped at MAX_STORED_TREES so
 * the cookie stays well under the ~4KB per-cookie browser limit.
 *
 * The cookie is keyed by the logged-in user so accounts sharing a browser
 * don't see each other's simulated trees.
 */

const COOKIE_PREFIX = "tt_simulated_trees"
const LEGACY_COOKIE_NAME = "tt_simulated_trees"
const COOKIE_DAYS = 365
const MAX_STORED_TREES = 20
const CHANGE_EVENT = "tt-simulated-trees-changed"

// Read directly (rather than importing the app's auth helpers)
function getLoggedInUser() {
    try {
        return localStorage.getItem("logged_in_user")
    } catch (e) {
        return null
    }
}

function cookieName() {
    const user = (getLoggedInUser() || "").toLowerCase().trim()
    return user ? `${COOKIE_PREFIX}_${encodeURIComponent(user)}` : null
}

function readCookie(name) {
    if (typeof document === "undefined") return null
    const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1")
    const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"))
    return match ? decodeURIComponent(match[1]) : null
}

function writeCookie(name, value, days) {
    if (typeof document === "undefined") return
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

function deleteCookie(name) {
    if (typeof document === "undefined") return
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`
}

function migrateLegacyCookie(name) {
    const legacy = readCookie(LEGACY_COOKIE_NAME)
    if (!legacy) return null
    try {
        if (!Array.isArray(JSON.parse(legacy))) return null
    } catch (e) {
        return null
    }
    writeCookie(name, legacy, COOKIE_DAYS)
    deleteCookie(LEGACY_COOKIE_NAME)
    return legacy
}

function notifyChanged() {
    if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(CHANGE_EVENT))
    }
}

/** Returns all simulated trees, newest first. */
export function getSimulatedTrees() {
    const name = cookieName()
    if (!name) return []
    const raw = readCookie(name) ?? migrateLegacyCookie(name)
    if (!raw) return []
    try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
    } catch (e) {
        console.error("Failed to parse simulated trees cookie:", e)
        return []
    }
}

/**
 * Saves a new simulated tree and returns the stored record.
 * `slotMap`/`totalLeaves` are the same inputs SimulatedTalkTreePreview takes.
 * `answers` (the raw 1–5 slider values keyed by question) is also stored so
 * the simulator can put the sliders back exactly where they were, rather
 * than only being able to redraw the resulting tree.
 */
export function saveSimulatedTree({ title, totalLeaves, slotMap, answers }) {
    const trees = getSimulatedTrees()
    const tree = {
        id: `sim-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title,
        createdAt: Date.now(),
        totalLeaves,
        slotMap,
        answers,
        isSimulated: true,
    }
    const name = cookieName()
    if (!name) return tree
    const next = [tree, ...trees].slice(0, MAX_STORED_TREES)
    writeCookie(name, JSON.stringify(next), COOKIE_DAYS)
    notifyChanged()
    return tree
}


export function deleteSimulatedTree(id) {
    const name = cookieName()
    if (!name) return
    const trees = getSimulatedTrees().filter((t) => t.id !== id)
    writeCookie(name, JSON.stringify(trees), COOKIE_DAYS)
    notifyChanged()
}

export function subscribeToSimulatedTrees(callback) {
    if (typeof window === "undefined") return () => {}
    window.addEventListener(CHANGE_EVENT, callback)
    return () => window.removeEventListener(CHANGE_EVENT, callback)
}